import axios from 'axios';

// Detectar si estamos en un subdominio de Kontrol
const hostname = window.location.hostname;
const isKontrolTenant = hostname.endsWith('.kontrol.farmavida.site');
const BACKEND_URL = isKontrolTenant 
  ? `https://${hostname}`
  : (process.env.REACT_APP_BACKEND_URL || 'https://farmavida.site');
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance with base configuration
const apiClient = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

// Request interceptor
apiClient.interceptors.request.use(
  (config) => {
    console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
    
    // Add authorization header if token exists
    const token = localStorage.getItem('farmavida_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor
apiClient.interceptors.response.use(
  (response) => {
    console.log(`API Response: ${response.status} ${response.config.url}`);
    return response;
  },
  (error) => {
    console.error('API Response Error:', error.response?.data || error.message);
    
    // Handle token expiration/invalid token
    if (error.response?.status === 401) {
      // Clear invalid token
      localStorage.removeItem('farmavida_token');
      
      // Redirect to login if not already there
      if (!window.location.pathname.includes('/login') && !window.location.pathname === '/') {
        window.location.href = '/';
      }
    }
    
    return Promise.reject(error);
  }
);

// API functions

// Dashboard & Metrics
export const getDashboardMetrics = async (startDate, endDate) => {
  const params = {};
  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;
  
  const response = await apiClient.get('/dashboard/metrics', { params });
  return response.data;
};

// Stores API
export const getStores = async () => {
  const response = await apiClient.get('/stores');
  return response.data;
};

export const getStore = async (storeId) => {
  const response = await apiClient.get(`/stores/${storeId}`);
  return response.data;
};

export const createStore = async (storeData) => {
  const response = await apiClient.post('/stores', storeData);
  return response.data;
};

export const updateStore = async (storeId, storeData) => {
  const response = await apiClient.put(`/stores/${storeId}`, storeData);
  return response.data;
};

export const deleteStore = async (storeId) => {
  const response = await apiClient.delete(`/stores/${storeId}`);
  return response.data;
};

// Sales API
export const getSales = async (filters = {}) => {
  const params = {};
  if (filters.storeId) params.store_id = filters.storeId;
  if (filters.startDate) params.start_date = filters.startDate;
  if (filters.endDate) params.end_date = filters.endDate;
  
  const response = await apiClient.get('/sales', { params });
  return response.data;
};

export const getSale = async (saleId) => {
  const response = await apiClient.get(`/sales/${saleId}`);
  return response.data;
};

export const createSale = async (saleData) => {
  const response = await apiClient.post('/sales', saleData);
  return response.data;
};

export const updateSale = async (saleId, saleData) => {
  const response = await apiClient.put(`/sales/${saleId}`, saleData);
  return response.data;
};

export const deleteSale = async (saleId) => {
  const response = await apiClient.delete(`/sales/${saleId}`);
  return response.data;
};

// Expenses API
export const getExpenses = async (filters = {}) => {
  const params = {};
  if (filters.category) params.category = filters.category;
  if (filters.storeId) params.store_id = filters.storeId;
  if (filters.startDate) params.start_date = filters.startDate;
  if (filters.endDate) params.end_date = filters.endDate;
  if (filters.reference) params.reference = filters.reference;
  
  const response = await apiClient.get('/expenses', { params });
  return response.data;
};

export const getExpense = async (expenseId) => {
  const response = await apiClient.get(`/expenses/${expenseId}`);
  return response.data;
};

export const createExpense = async (expenseData) => {
  const response = await apiClient.post('/expenses', expenseData);
  return response.data;
};

export const deleteExpense = async (expenseId) => {
  const response = await apiClient.delete(`/expenses/${expenseId}`);
  return response.data;
};

// Inventory API
export const getInventoryPurchases = async (filters = {}) => {
  const params = {};
  if (filters.storeId) params.store_id = filters.storeId;
  if (filters.startDate) params.start_date = filters.startDate;
  if (filters.endDate) params.end_date = filters.endDate;
  
  const response = await apiClient.get('/inventory', { params });
  return response.data;
};

export const getInventoryPurchase = async (purchaseId) => {
  const response = await apiClient.get(`/inventory/${purchaseId}`);
  return response.data;
};

export const createInventoryPurchase = async (purchaseData) => {
  const response = await apiClient.post('/inventory', purchaseData);
  return response.data;
};

export const updateInventoryPurchase = async (purchaseId, purchaseData) => {
  const response = await apiClient.put(`/inventory/${purchaseId}`, purchaseData);
  return response.data;
};

export const deleteInventoryPurchase = async (purchaseId) => {
  const response = await apiClient.delete(`/inventory/${purchaseId}`);
  return response.data;
};

// Supplier Management API
export const getSuppliers = async () => {
  const response = await apiClient.get('/suppliers');
  return response.data;
};

export const mergeSuppliers = async (oldName, newName) => {
  const response = await apiClient.post('/suppliers/merge', null, {
    params: { old_name: oldName, new_name: newName }
  });
  return response.data;
};

export const deleteSupplier = async (supplierName) => {
  const response = await apiClient.delete(`/suppliers/${encodeURIComponent(supplierName)}`);
  return response.data;
};


// Accounts Payable API
export const getAccountsPayable = async (storeId = null, startDate = null, endDate = null) => {
  const params = {};
  if (storeId) params.store_id = storeId;
  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;
  
  // Increase timeout for this endpoint as it processes many records
  const response = await apiClient.get('/accounts-payable', { 
    params,
    timeout: 30000  // 30 seconds timeout
  });
  return response.data;
};

export const getAccountsPayableSummary = async () => {
  const response = await apiClient.get('/accounts-payable/summary');
  return response.data;
};

export const createPaymentRecord = async (paymentData) => {
  const response = await apiClient.post('/payments', paymentData);
  return response.data;
};

export const createBulkPayment = async (bulkData) => {
  const response = await apiClient.post('/payments/bulk', bulkData);
  return response.data;
};

export const getPaymentsForPurchase = async (purchaseId) => {
  const response = await apiClient.get(`/payments/${purchaseId}`);
  return response.data;
};

export const deletePaymentRecord = async (paymentId) => {
  const response = await apiClient.delete(`/payments/${paymentId}`);
  return response.data;
};

export const uploadPaymentDocument = async (paymentId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  
  const response = await apiClient.post(`/upload-payment-document/${paymentId}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export const downloadPaymentDocument = async (paymentId) => {
  const response = await apiClient.get(`/download-payment-document/${paymentId}`, {
    responseType: 'blob',
  });
  return response.data;
};

export const deletePaymentDocument = async (paymentId) => {
  const response = await apiClient.delete(`/delete-payment-document/${paymentId}`);
  return response.data;
};

// Paid Invoices API
export const getPaidInvoices = async (filters = {}) => {
  const params = {};
  if (filters.storeId) params.store_id = filters.storeId;
  if (filters.startDate) params.start_date = filters.startDate;
  if (filters.endDate) params.end_date = filters.endDate;
  if (filters.supplierName) params.supplier_name = filters.supplierName;
  if (filters.paymentMethod) params.payment_method = filters.paymentMethod;
  if (filters.limit) params.limit = filters.limit;
  
  const response = await apiClient.get('/paid-invoices', { params });
  return response.data;
};

// Exchange Rate API
export const getExchangeRates = async (limit = 30) => {
  const params = { limit };
  const response = await apiClient.get('/exchange-rates', { params });
  return response.data;
};

export const getLatestExchangeRate = async () => {
  const response = await apiClient.get('/exchange-rates/latest');
  return response.data;
};

export const createExchangeRate = async (rateData) => {
  const response = await apiClient.post('/exchange-rates', rateData);
  return response.data;
};

export const deleteExchangeRate = async (rateId) => {
  const response = await apiClient.delete(`/exchange-rates/${rateId}`);
  return response.data;
};

// Reports API
export const getSalesByStore = async (startDate, endDate) => {
  const params = {};
  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;
  
  const response = await apiClient.get('/reports/sales-by-store', { params });
  return response.data;
};

export const getExpensesByCategory = async (startDate, endDate) => {
  const params = {};
  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;
  
  const response = await apiClient.get('/reports/expenses-by-category', { params });
  return response.data;
};

export const getProfitLossReport = async (startDate, endDate) => {
  const params = {};
  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;
  
  const response = await apiClient.get('/reports/profit-loss', { params });
  return response.data;
};

export const getStoreProfitability = async (startDate, endDate) => {
  const params = {};
  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;
  
  const response = await apiClient.get('/reports/store-profitability', { params });
  return response.data;
};

export const getPaymentMethodsReport = async (filters = {}) => {
  const params = {};
  if (filters.storeId) params.store_id = filters.storeId;
  if (filters.startDate) params.start_date = filters.startDate;
  if (filters.endDate) params.end_date = filters.endDate;
  if (filters.methods && filters.methods.length > 0) {
    params.methods = filters.methods.join(',');
  }
  
  const response = await apiClient.get('/reports/payment-methods', { params });
  return response.data;
};

export const getSupplierPurchasesReport = async (filters = {}) => {
  const params = {};
  if (filters.storeId) params.store_id = filters.storeId;
  if (filters.supplierName) params.supplier_name = filters.supplierName;
  if (filters.startDate) params.start_date = filters.startDate;
  if (filters.endDate) params.end_date = filters.endDate;
  
  const response = await apiClient.get('/reports/supplier-purchases', { params });
  return response.data;
};

export const updatePurchase = async (purchaseId, purchaseData) => {
  const response = await apiClient.put(`/inventory/${purchaseId}`, purchaseData);
  return response.data;
};
export const getUsers = async () => {
  const response = await apiClient.get('/users');
  return response.data;
};

export const createUser = async (userData) => {
  const response = await apiClient.post('/auth/register', userData);
  return response.data;
};

export const updateUser = async (userId, userData) => {
  const response = await apiClient.put(`/users/${userId}`, userData);
  return response.data;
};

export const updateUserStatus = async (userId, isActive) => {
  const response = await apiClient.patch(`/users/${userId}`, {
    is_active: isActive
  });
  return response.data;
};

export const deleteUser = async (userId) => {
  const response = await apiClient.delete(`/users/${userId}`);
  return response.data;
};

// Break-Even Analysis API
export const createBreakEvenData = async (breakEvenData) => {
  const response = await apiClient.post('/break-even', breakEvenData);
  return response.data;
};

export const getBreakEvenData = async (filters = {}) => {
  const params = {};
  if (filters.storeId) params.store_id = filters.storeId;
  if (filters.periodMonth) params.period_month = filters.periodMonth;
  if (filters.periodYear) params.period_year = filters.periodYear;
  
  const response = await apiClient.get('/break-even', { params });
  return response.data;
};

export const updateBreakEvenData = async (breakEvenId, breakEvenData) => {
  const response = await apiClient.put(`/break-even/${breakEvenId}`, breakEvenData);
  return response.data;
};

export const deleteBreakEvenData = async (breakEvenId) => {
  const response = await apiClient.delete(`/break-even/${breakEvenId}`);
  return response.data;
};

export const getBreakEvenAnalysis = async (storeId, periodMonth, periodYear) => {
  const params = { period_month: periodMonth, period_year: periodYear };
  const response = await apiClient.get(`/break-even/${storeId}/analysis`, { params });
  return response.data;
};

// Initial Inventory Value API
export const createInitialInventoryValue = async (inventoryData) => {
  const response = await apiClient.post('/initial-inventory', inventoryData);
  return response.data;
};

export const getInitialInventoryValues = async (filters = {}) => {
  const params = {};
  if (filters.storeId) params.store_id = filters.storeId;
  if (filters.periodMonth) params.period_month = filters.periodMonth;
  if (filters.periodYear) params.period_year = filters.periodYear;
  
  const response = await apiClient.get('/initial-inventory', { params });
  return response.data;
};

export const updateInitialInventoryValue = async (inventoryId, inventoryData) => {
  const response = await apiClient.put(`/initial-inventory/${inventoryId}`, inventoryData);
  return response.data;
};

export const deleteInitialInventoryValue = async (inventoryId) => {
  const response = await apiClient.delete(`/initial-inventory/${inventoryId}`);
  return response.data;
};

export const getStoreFinancialBalance = async (storeId, periodMonth, periodYear) => {
  const params = { period_month: periodMonth, period_year: periodYear };
  const response = await apiClient.get(`/initial-inventory/${storeId}/balance`, { params });
  return response.data;
};

// WhatsApp Service API (through backend proxy)
export const getWhatsAppStatus = async () => {
  const response = await apiClient.get('/whatsapp/status');
  return response.data;
};

export const getWhatsAppQR = async () => {
  const response = await apiClient.get('/whatsapp/qr');
  return response.data;
};

export const restartWhatsAppService = async () => {
  const response = await apiClient.post('/whatsapp/restart');
  return response.data;
};

export const sendTestNotification = async () => {
  const response = await apiClient.post('/whatsapp/send-test-message');
  return response.data;
};

// Cash Control API
export const getCashBalance = async () => {
  const response = await apiClient.get('/cash-control/balance');
  return response.data;
};

export const getCashMovements = async (startDate, endDate, movementType) => {
  const params = {};
  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;
  if (movementType) params.movement_type = movementType;
  
  const response = await apiClient.get('/cash-control/movements', { params });
  return response.data;
};

export const createCashAdjustment = async (adjustmentData) => {
  const response = await apiClient.post('/cash-control/adjustment', adjustmentData);
  return response.data;
};

export const createCashArqueo = async (arqueoData) => {
  const response = await apiClient.post('/cash-control/arqueo', arqueoData);
  return response.data;
};

export const createDepositoBanco = async (data) => {
  const response = await apiClient.post('/cash-control/deposito-banco', data);
  return response.data;
};

export const getDailyCashReport = async (date) => {
  const response = await apiClient.get('/cash-control/daily-report', {
    params: { date }
  });
  return response.data;
};

export default apiClient;